import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import { Spinner, Badge, Button, Form, Collapse, Card, Row, Col } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import { FaPlus, FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { FiSearch, FiRefreshCw, FiToggleLeft, FiToggleRight, FiDollarSign } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment-timezone";

const CollectionFee = () => {
    const [fees, setFees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [showCollapsible, setShowCollapsible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const fetchingRef = useRef(false);

    const [formData, setFormData] = useState({
        range_min: "",
        range_max: "",
        collection_fee: "",
        status: 1
    });

    const fetchFees = useCallback(async () => {
        if (fetchingRef.current) return;
        fetchingRef.current = true;

        try {
            setLoading(true);
            const res = await axios.get("https://www.azzenterprises.com/api/fee/collectionfees", { withCredentials: true });
            setFees(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            toast.error("Failed to fetch fees");
            setFees([]);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    }, []);

    useEffect(() => {
        fetchFees();
    }, [fetchFees]);

    const resetForm = () => {
        setFormData({
            range_min: "",
            range_max: "",
            collection_fee: "",
            status: 1
        });
        setEditMode(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (parseFloat(formData.range_min) >= parseFloat(formData.range_max)) {
            toast.error("Range max must be greater than range min");
            return;
        }

        try {
            const payload = {
                range_min: parseFloat(formData.range_min),
                range_max: parseFloat(formData.range_max),
                collection_fee: parseFloat(formData.collection_fee),
                status: formData.status
            };

            if (editMode) {
                await axios.put(`https://www.azzenterprises.com/api/fee/collectionfees/${formData.id}`, payload, { withCredentials: true });
                toast.success("Fee updated!");
            } else {
                await axios.post("https://www.azzenterprises.com/api/fee/collectionfees", payload, { withCredentials: true });
                toast.success("Fee created!");
            }

            fetchFees();
            resetForm();
            setShowCollapsible(false);
        } catch (err) {
            const message = err?.response?.data?.message || err?.message || "Failed to save fee";
            toast.error(message);
        }
    };

    const handleEdit = (fee) => {
        setFormData({
            id: fee.id,
            range_min: fee.range_min,
            range_max: fee.range_max,
            collection_fee: fee.collection_fee,
            status: fee.status
        });
        setEditMode(true);
        setShowCollapsible(true);
    };

    const handleCancel = () => {
        resetForm();
        setShowCollapsible(false);
    };

    const handleStatusToggle = async (id, currentStatus) => {
        try {
            const newStatus = currentStatus === 1 ? 0 : 1;
            await axios.put(`https://www.azzenterprises.com/api/fee/collectionfees/${id}`, { status: newStatus }, { withCredentials: true });
            toast.success(`Status ${newStatus === 1 ? "activated" : "deactivated"}`);
            fetchFees();
        } catch (err) {
            toast.error("Failed to update status");
        }
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        try {
            return moment(date).format('DD/MM/YY hh:mm A');
        } catch (error) {
            return 'Invalid';
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            width: "50px",
            center: true,
        },
        {
            name: "RANGE",
            selector: (row) => `${formatCurrency(row.range_min)} - ${formatCurrency(row.range_max)}`,
            sortable: true,
            cell: (row) => (
                <div className="fw-medium" style={{ fontSize: "0.75rem" }}>
                    ${formatCurrency(row.range_min)} - ${formatCurrency(row.range_max)}
                </div>
            ),
            width: "180px",
        },
        {
            name: "FEE",
            selector: (row) => row.collection_fee,
            sortable: true,
            cell: (row) => (
                <Badge bg="primary" className="px-2 py-1 fw-normal" style={{ fontSize: "0.7rem" }}>
                    ${formatCurrency(row.collection_fee)}
                </Badge>
            ),
            width: "100px",
            center: true,
        },
        {
            name: "CREATED",
            selector: (row) => formatDate(row.created_time),
            sortable: true,
            cell: (row) => (
                <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                    {formatDate(row.created_time)}
                </div>
            ),
            width: "150px",
        },
        {
            name: "UPDATED",
            selector: (row) => formatDate(row.updated_time),
            sortable: true,
            cell: (row) => (
                <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                    {formatDate(row.updated_time)}
                </div>
            ),
            width: "150px",
        },
        {
            name: "STATUS",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => {
                const isActive = row.status === 1;
                return (
                    <div className="d-flex align-items-center gap-1">
                        <Badge bg={isActive ? "success" : "warning"} className="py-1 px-2" style={{ fontSize: "0.75rem" }}>
                            {isActive ? "Active" : "Inactive"}
                        </Badge>
                        <span
                            role="button"
                            onClick={() => handleStatusToggle(row.id, row.status)}
                            style={{ cursor: "pointer", fontSize: "1rem" }}
                            title={isActive ? "Deactivate" : "Activate"}
                        >
                            {isActive ? <FiToggleRight className="text-success" /> : <FiToggleLeft className="text-warning" />}
                        </span>
                    </div>
                );
            },
            minWidth: "110px",
            right: true,
        },
        {
            name: "ACTION",
            cell: (row) => (
                <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleEdit(row)}
                    title="Edit"
                    className="px-2 py-1"
                    style={{ fontSize: "0.7rem" }}
                >
                    <FaEdit size={10} />
                </Button>
            ),
            ignoreRowClick: true,
            width: "70px",
            right: true,
        }
    ];

    const filteredFees = Array.isArray(fees)
        ? fees.filter(fee => {
            if (!fee) return false;
            return (
                (fee.range_min?.toString() || '').includes(searchTerm) ||
                (fee.range_max?.toString() || '').includes(searchTerm) ||
                (fee.collection_fee?.toString() || '').includes(searchTerm)
            );
        })
        : [];

    return (
        <div className="container-fluid py-2" style={{ fontSize: "0.8rem" }}>
            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            {/* <h6 className="fw-bold mb-0" style={{ fontSize: "0.9rem" }}>Processing Fees</h6> */}
                            <p className="text-muted">Configure fees based on amount ranges</p>
                        </div>
                        <div className="d-flex gap-1">
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                className="d-flex align-items-center gap-1 border py-1 px-2"
                                onClick={fetchFees}
                                disabled={loading}
                                style={{ fontSize: "0.7rem" }}
                            >
                                <FiRefreshCw size={11} />
                                Refresh
                            </Button>
                            <Button
                                variant={showCollapsible ? "outline-secondary" : "primary"}
                                size="sm"
                                className="d-flex align-items-center gap-1 border py-1 px-2"
                                onClick={() => showCollapsible ? handleCancel() : (resetForm(), setShowCollapsible(true))}
                                style={{ fontSize: "0.7rem" }}
                            >
                                {showCollapsible ? <FaTimes size={11} /> : <FaPlus size={11} />}
                                {showCollapsible ? "Cancel" : "Set Fee"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <Collapse in={showCollapsible}>
                <div>
                    <Card className="mb-3 border border-primary-subtle">
                        <Card.Body className="p-3">
                            <h6 className="card-title mb-3 fw-semibold" style={{ fontSize: "0.85rem" }}>
                                {editMode ? "Edit Processing Fee" : "New Processing Fee"}
                            </h6>
                            <Form onSubmit={handleSubmit}>
                                <Row className="g-2">
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-medium mb-1" style={{ fontSize: "0.75rem" }}>Min ($)</Form.Label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-white py-1 px-2" style={{ fontSize: "0.7rem" }}>$</span>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={formData.range_min}
                                                    onChange={(e) => setFormData({ ...formData, range_min: e.target.value })}
                                                    placeholder="0.00"
                                                    required
                                                    className="py-1 px-2 border-start-0"
                                                    style={{ fontSize: "0.75rem" }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-medium mb-1" style={{ fontSize: "0.75rem" }}>Max ($)</Form.Label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-white py-1 px-2" style={{ fontSize: "0.7rem" }}>$</span>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={formData.range_max}
                                                    onChange={(e) => setFormData({ ...formData, range_max: e.target.value })}
                                                    placeholder="0.00"
                                                    required
                                                    className="py-1 px-2 border-start-0"
                                                    style={{ fontSize: "0.75rem" }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-medium mb-1" style={{ fontSize: "0.75rem" }}>Fee ($)</Form.Label>
                                            <div className="input-group input-group-sm">
                                                <span className="input-group-text bg-white py-1 px-2" style={{ fontSize: "0.7rem" }}>$</span>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={formData.collection_fee}
                                                    onChange={(e) => setFormData({ ...formData, collection_fee: e.target.value })}
                                                    placeholder="0.00"
                                                    required
                                                    className="py-1 px-2 border-start-0"
                                                    style={{ fontSize: "0.75rem" }}
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-medium mb-1" style={{ fontSize: "0.75rem" }}>Status</Form.Label>
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge bg={formData.status === 1 ? "success" : "warning"} className="py-1 px-2" style={{ fontSize: "0.75rem" }}>
                                                    {formData.status === 1 ? "Active" : "Inactive"}
                                                </Badge>
                                                <span
                                                    role="button"
                                                    onClick={() => setFormData({ ...formData, status: formData.status === 1 ? 0 : 1 })}
                                                    style={{ cursor: "pointer", fontSize: "1.2rem" }}
                                                >
                                                    {formData.status === 1 ? <FiToggleRight className="text-success" /> : <FiToggleLeft className="text-warning" />}
                                                </span>
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end gap-2 mt-3">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={handleCancel}
                                        className="d-flex align-items-center gap-1 py-1 px-2"
                                        style={{ fontSize: "0.7rem" }}
                                    >
                                        <FaTimes size={10} />
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant={editMode ? "warning" : "primary"}
                                        size="sm"
                                        className="d-flex align-items-center gap-1 py-1 px-2"
                                        style={{ fontSize: "0.7rem" }}
                                    >
                                        <FaSave size={10} />
                                        {editMode ? "Update" : "Create"}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </div>
            </Collapse>

            <div className="row mb-3">
                <div className="col-12">
                    <div className="input-group input-group-sm" style={{ maxWidth: "350px" }}>
                        <span className="input-group-text bg-white border-end-0 py-1 px-2">
                            <FiSearch size={12} className="text-gray-500" />
                        </span>
                        <input
                            type="text"
                            className="form-control border-start-0 py-1 px-2"
                            placeholder="Search ranges..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ fontSize: "0.75rem" }}
                        />
                        {searchTerm && (
                            <button
                                className="btn btn-outline-danger border-start-0 py-1 px-2"
                                type="button"
                                onClick={() => setSearchTerm('')}
                                aria-label="Clear search"
                                style={{ fontSize: "0.7rem" }}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-2 border-bottom bg-gray-50">
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted fw-medium">Processing Fee List</small>
                            <small className="text-muted">{filteredFees.length} records</small>
                        </div>
                    </div>

                    <div className="p-2">
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center py-4">
                                <Spinner animation="border" size="sm" variant="secondary" />
                                <span className="ms-2 text-muted" style={{ fontSize: "0.75rem" }}>Loading...</span>
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={filteredFees}
                                pagination
                                paginationPerPage={10}
                                paginationRowsPerPageOptions={[5, 10, 15, 20]}
                                paginationComponentOptions={{
                                    rowsPerPageText: 'Rows:',
                                    rangeSeparatorText: 'of',
                                }}
                                highlightOnHover
                                responsive
                                dense
                                persistTableHead
                                fixedHeader
                                fixedHeaderScrollHeight="calc(100vh - 250px)"
                                customStyles={{
                                    headCells: {
                                        style: {
                                            backgroundColor: "#f8f9fa",
                                            fontWeight: "600",
                                            fontSize: "0.7rem",
                                            textTransform: "uppercase",
                                            color: "#495057",
                                            borderBottom: "1px solid #dee2e6",
                                            paddingTop: "8px",
                                            paddingBottom: "8px",
                                        },
                                    },
                                    cells: {
                                        style: {
                                            fontSize: "0.75rem",
                                            paddingTop: "6px",
                                            paddingBottom: "6px",
                                            color: "#212529"
                                        }
                                    },
                                    rows: {
                                        style: {
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#f8f9fa'
                                            },
                                            minHeight: '35px',
                                        }
                                    },
                                    pagination: {
                                        style: {
                                            fontSize: "0.7rem",
                                            paddingTop: "10px",
                                            borderTop: "1px solid #dee2e6",
                                        },
                                    },
                                }}
                                noDataComponent={
                                    <div className="text-center py-4" style={{ fontSize: "0.75rem" }}>
                                        <FiDollarSign size={32} className="text-muted mb-2" />
                                        <h6 className="text-muted mb-2">No fees found</h6>
                                        <p className="text-muted mb-3">Click "Set Fee" to create your first fee range</p>
                                    </div>
                                }
                            />
                        )}
                    </div>
                </Card.Body>
            </Card>

            <ToastContainer
                position="top-right"
                autoClose={1500}
                newestOnTop
                theme="light"
                style={{ fontSize: "0.8rem" }}
            />
        </div>
    );
};

export default CollectionFee;